import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import type { ListActivePaymentDto } from '../../generated-api';
import { PaymentState } from '../../generated-api';
import useIntl from '../hooks/useIntl';

interface Props {
  payment?: ListActivePaymentDto;
  label?: string;
  statusText?: string;
  showCountdown?: boolean;
  onDismiss?: () => void;
  swipeUp?: boolean;
}

export const COUNTDOWN_DURATION_MS = 8000;
const SWIPE_THRESHOLD = 50;

const PaymentToastContent: FC<Props> = ({ payment, label, statusText, showCountdown, onDismiss, swipeUp }) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const toastElRef = useRef<HTMLElement | null>(null);
  const touchStartY = useRef<number>(0);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Find and store the .toast element
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    let el: HTMLElement | null = ref.current;
    while (el && !el.classList.contains('toast')) {
      el = el.parentElement;
    }
    if (!el) {
      return;
    }
    toastElRef.current = el;
  }, []);

  // Countdown bar
  useEffect(() => {
    const toastEl = toastElRef.current;
    if (!showCountdown || !toastEl) {
      return;
    }

    toastEl.style.position = 'relative';
    toastEl.style.paddingBottom = '22px';

    const track = document.createElement('div');
    track.style.cssText = 'position:absolute;bottom:0;left:0;right:0;height:10px;pointer-events:none;';

    const fill = document.createElement('div');
    fill.style.cssText = `position:absolute;bottom:0;left:0;right:0;height:4px;background:rgba(0,0,0,0.25);transform-origin:left;animation:toastCountdown ${COUNTDOWN_DURATION_MS}ms linear forwards;`;

    track.appendChild(fill);
    toastEl.prepend(track);

    return () => {
      track.remove();
      toastEl.style.position = '';
      toastEl.style.paddingBottom = '';
    };
  }, [showCountdown]);

  // Dismiss X button
  useEffect(() => {
    const toastEl = toastElRef.current;
    if (!onDismissRef.current || !toastEl) {
      return;
    }

    toastEl.style.paddingRight = '0';

    const dismissButton = document.createElement('span');
    dismissButton.textContent = '✕';
    dismissButton.style.cssText =
      'display:inline-flex;align-items:center;align-self:stretch;padding:6px 14px;margin-left:6px;margin-top:-6px;margin-bottom:-6px;border-left:1px solid rgba(0,0,0,0.15);cursor:pointer;flex-shrink:0;color:#0e7d50;';

    dismissButton.addEventListener('click', (e) => {
      e.stopPropagation();
      onDismissRef.current?.();
    });

    toastEl.appendChild(dismissButton);

    return () => {
      dismissButton.remove();
      toastEl.style.paddingRight = '';
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    const toastEl = toastElRef.current;
    if (toastEl) {
      toastEl.style.transition = '';
    }
    touchStartY.current = e.touches[0]?.clientY ?? 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const toastEl = toastElRef.current;
    if (!toastEl) {
      return;
    }
    const delta = (e.touches[0]?.clientY ?? touchStartY.current) - touchStartY.current;
    if (swipeUp ? delta > 0 : delta < 0) {
      return;
    }
    toastEl.style.transform = `translateY(${delta}px)`;
    toastEl.style.opacity = String(Math.max(0.3, 1 - Math.abs(delta) / 150));
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const toastEl = toastElRef.current;
    const endY = e.changedTouches[0]?.clientY ?? touchStartY.current;
    const delta = endY - touchStartY.current;
    const triggered = swipeUp ? delta < -SWIPE_THRESHOLD : delta > SWIPE_THRESHOLD;

    if (triggered) {
      if (toastEl) {
        toastEl.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
        toastEl.style.transform = `translateY(${swipeUp ? -300 : 300}px)`;
        toastEl.style.opacity = '0';
      }
      onDismiss?.();
    } else if (toastEl) {
      toastEl.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
      toastEl.style.transform = 'translateY(0)';
      toastEl.style.opacity = '1';
      setTimeout(() => {
        if (toastElRef.current) {
          toastElRef.current.style.transition = '';
        }
      }, 200);
    }
  };

  const methodLabel = label ?? payment?.paymentMethod.label;

  const statusLabel = () => {
    if (statusText) {
      return statusText;
    }

    if (payment?.paymentState === PaymentState.SUCCEEDED) {
      return intl.formatMessage({ id: 'toast.paymentSuccess' });
    }

    if (payment?.paymentState === PaymentState.FAILED) {
      return intl.formatMessage({ id: 'toast.paymentError' });
    }

    return '';
  };

  return (
    <div ref={ref} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      <span className="text-small">
        <span className="text-extra-bold">{methodLabel}</span>
        {' - '}
        {statusLabel()}
      </span>
    </div>
  );
};

export default PaymentToastContent;
